<template>
  <div class="method-stripe">
    <b-input-group prepend="$" class="mb-1">
      <b-form-input
        :placeholder="`Enter Amount ${limits.min}$ - ${limits.max}$`"
        type="number"
        v-model="amount"
      />
    </b-input-group>

    <b-alert variant="info" show class="mb-1 py-1 px-1" style="font-size: 13px;">
      <b>Any Visa / Mastercard is supported.</b> Payment is processed securely by <strong>Revolut</strong>.
    </b-alert><br><br>

    <!-- DISABLED 2026-05: 2% processing fee no longer charged to customer.
         Seller absorbs the Revolut fee. Uncomment if you want to re-enable.
    Note: you will pay {{ fee }}% processing fee<br />
    <br />
    -->
    <strong> You'll receive an invoice by email after payment. </strong>
    <br /><br />

    <span v-if="amount > 0">
      <h3>
        Total: <strong>{{ totalAmount }} $</strong>
      </h3>
    </span>


    <div class="text-error" v-if="amountValue > 0 && amountValue < limits.min">
      Can't pay less than {{ limits.min }}$
    </div>

    <div class="text-error" v-if="amountValue > limits.max">
      Can't pay more than {{ limits.max }}$
    </div>
    <br />

    <b-button
      v-show="amountValue >= limits.min && amountValue <= limits.max"
      variant="relief-success"
      class="btn btn-primary btn-lg btn-block"
      @click="submit"
      :disabled="loading"
    >
      <b-spinner v-if="loading" small />
      Pay
    </b-button>

    <!-- ✅ UPDATED: Modern, clean modal (no extra buttons), blue CTA -->
    <b-modal
      v-model="showPaymentModal"
      hide-footer
      centered
      size="md"
      modal-class="pay-modal"
      content-class="pay-modal__content"
      header-class="pay-modal__header"
      body-class="pay-modal__body"
    >
      <!-- ✅ Custom header (modern look). We hide the default title and render our own -->
      <template #modal-title>
        <div class="pay-modal__titlewrap">
          <div class="pay-modal__icon" aria-hidden="true">!</div>
          <div>
            <div class="pay-modal__title">Payment window blocked</div>
            <div class="pay-modal__subtitle">
              Click below to open the payment page in a new tab.
            </div>
          </div>
        </div>
      </template>

      <!-- ✅ Body -->
      <div class="pay-modal__card">
        <div class="pay-modal__row">
          <div class="pay-modal__label">Amount</div>
          <div class="pay-modal__value">${{ totalAmount }}</div>
        </div>

        <div class="pay-modal__hint">
          After you complete payment, return to IMEICheck.com to continue your IMEI
          check.
        </div>

        <!-- ✅ Blue CTA button -->
        <b-button
          :href="paymentLink"
          target="_blank"
          rel="noopener"
          variant="primary"
          class="pay-modal__cta"
        >
          Open payment
        </b-button>

        <!-- ✅ Tiny secondary text (no extra button) -->
        <div class="pay-modal__micro">
          If nothing happens, allow pop-ups for this site and try again.
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BButton, BFormInput, BInputGroup, BModal } from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { mapMutations } from "vuex";
import Deposit from "@/services/deposit.service";
import loader from "../../../components/FullScreenLoader.vue";

export default {
  components: {
    loader,
    BButton,
    BInputGroup,
    BFormInput,
    BModal,
    ToastificationContent,
  },
  props: ["between"],
  data: () => ({
    amount: "",
    fee: 0, // DISABLED 2026-05: was 2 (2% processing fee passed to customer).
            // Set back to 2 to re-enable fee passthrough.
    loading: false,
    verifyInterval: null,

    // ✅ modal state + link
    showPaymentModal: false,
    paymentLink: "",
  }),
  computed: {
    limits() {
      // Revolut (instant) limits
      return { min: 5, max: 500 };
    },
    amountValue() {
      return Number(this.amount) || 0;
    },
    totalAmount() {
      // With fee=0 this is just `amountValue` rounded to 2 decimals.
      // The expression `amountValue + amountValue * (fee/100)` is preserved so
      // re-enabling the fee is a one-line change in `data().fee`.
      return Number(
        this.amountValue + this.amountValue * (this.fee / 100)
      ).toFixed(2);
    },
  },
  methods: {
    async submit() {
      this.loading = true;

      // ✅ pre-open tab synchronously to reduce popup blocking
      const openedTab = window.open("about:blank", "_blank");
    //  const openedTab = null;

      try {
        const { order } = await Deposit.revolutCreateOrder({
          price: this.totalAmount,
        });

        const checkoutUrl = order.checkout_url;

        if (openedTab && !openedTab.closed) {
          openedTab.location.replace(checkoutUrl);
          openedTab.focus && openedTab.focus();
        } else {
          // ✅ popup blocked -> show modal with payment link
          this.paymentLink = checkoutUrl;
          this.showPaymentModal = true;

          // Optional same-tab fallback:
          // window.location.href = checkoutUrl;
        }
      } catch (error) {
        if (openedTab && !openedTab.closed) openedTab.close();
      } finally {
        this.loading = false;
      }
    },
    clearVerifyInterval() {
      clearInterval(this.verifyInterval);
      this.loading = false;
    },
    ...mapMutations(["HANDLE_CREDIT"]),
    complete(amount) {
      this.HANDLE_CREDIT(+amount);
      this.$toast({
        component: ToastificationContent,
        props: {
          title: `Deposit Made`,
          icon: "CheckIcon",
          text: `You have deposit ${amount}$`,
          variant: "success",
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
p {
  border: 1px dashed;
  padding: 20px;
  background: #161e312b;
  text-align: left;
}

/* ✅ ADDED: Modern modal styling (minimal, but big visual upgrade) */
.pay-modal__content {
  border: 0;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.25);
}

.pay-modal__header {
  border: 0;
  padding: 18px 18px 8px;
  background: linear-gradient(180deg, rgba(59, 130, 246, 0.12), rgba(59, 130, 246, 0));
}

.pay-modal__body {
  padding: 14px 18px 18px;
}

.pay-modal__titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pay-modal__icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  font-weight: 800;
  color: #1d4ed8;
  background: rgba(59, 130, 246, 0.18);
}

.pay-modal__title {
  font-size: 16px;
  font-weight: 800;
  line-height: 1.2;
}

.pay-modal__subtitle {
  margin-top: 2px;
  font-size: 13px;
  opacity: 0.75;
}

.pay-modal__card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 14px;
}

.pay-modal__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 10px;
}

.pay-modal__label {
  font-size: 12px;
  opacity: 0.75;
}

.pay-modal__value {
  font-size: 18px;
  font-weight: 800;
}

.pay-modal__hint {
  font-size: 13px;
  opacity: 0.85;
  margin: 10px 0 14px;
}

.pay-modal__cta {
  width: 100%;
  padding: 12px 14px;
  border-radius: 12px;
  font-weight: 800;
  letter-spacing: 0.2px;
}

.pay-modal__micro {
  margin-top: 10px;
  font-size: 12px;
  opacity: 0.7;
}
</style>
